/**
 * Hook for synchronizing Platform Usage page state with URL parameters.
 * Uses useSearchParams as the single source of truth - no duplicate React state.
 */

import { useCallback, useMemo } from 'react';

import { Grouping as H2OGPTeGrouping } from '../../../telemetry/gen/ai/h2o/cloud/telemetry_read_api/h2ogpte/v1/h2ogpte_pb';
import { Granularity, Grouping } from '../../../telemetry/gen/ai/h2o/cloud/telemetry_read_api/v1/aiunits_pb';
import { useSearchParams } from '../../../utils/hooks';
import { ResourceType } from '../constants/resource';
import { DateRangeType } from '../types';
import { getDefaultGranularity, getDefaultTimeRange } from '../utils/timeRangePresets';
import {
  formatDateParam,
  formatGranularityParam,
  formatGroupByParam,
  formatH2OGPTeGroupByParam,
  formatResourceParam,
  parseDateParam,
  parseGranularityParam,
  parseGroupByParam,
  parseH2OGPTeGroupByParam,
  parseResourceParam,
  URL_PARAMS,
} from '../utils/urlParams';

// Default values
const DEFAULT_RESOURCE = ResourceType.AI_UNITS;
const DEFAULT_GROUPBY = Grouping.WORKSPACE;
const DEFAULT_H2OGPTE_GROUPBY = H2OGPTeGrouping.WORKSPACE;
const DEFAULT_GRANULARITY = getDefaultGranularity();
const DEFAULT_DATE_RANGE_TYPE: DateRangeType = '1month';

// Get default time range
const defaultRange = getDefaultTimeRange();
const DEFAULT_START_TIME = defaultRange.startTime;
const DEFAULT_END_TIME = defaultRange.endTime;

export interface PlatformUsageUrlSyncResult {
  // Resource type
  resourceType: ResourceType;
  setResourceType: (resource: ResourceType) => void;

  // AI Units grouping
  groupBy: Grouping;
  setGroupBy: (groupBy: Grouping) => void;

  // H2OGPTe grouping
  h2ogpteGroupBy: H2OGPTeGrouping;
  setH2OGPTeGroupBy: (groupBy: H2OGPTeGrouping) => void;

  // Date range state (directly from URL - single source of truth)
  startTime: Date;
  endTime: Date;
  granularity: Granularity;
  dateRangeType: DateRangeType;

  // Callback to update URL when date range changes
  setDateRange: (startTime: Date, endTime: Date, granularity: Granularity, dateRangeType: DateRangeType) => void;
}

/**
 * Hook that manages Platform Usage page state directly from URL parameters.
 *
 * Uses useSearchParams as the single source of truth:
 * - All values are read directly from URL via useMemo
 * - All setters update URL via setSearchParams
 * - No duplicate React state (no useState for URL-persisted values)
 */
export function usePlatformUsageUrlSync(): PlatformUsageUrlSyncResult {
  const [searchParams, setSearchParams] = useSearchParams();

  // ============================================
  // Read values from URL (single source of truth)
  // ============================================

  const resourceType = useMemo(() => {
    const parsed = parseResourceParam(searchParams.get(URL_PARAMS.RESOURCE));
    return parsed ?? DEFAULT_RESOURCE;
  }, [searchParams]);

  const groupBy = useMemo(() => {
    const parsed = parseGroupByParam(searchParams.get(URL_PARAMS.GROUP_BY));
    return parsed ?? DEFAULT_GROUPBY;
  }, [searchParams]);

  const h2ogpteGroupBy = useMemo(() => {
    const parsed = parseH2OGPTeGroupByParam(searchParams.get(URL_PARAMS.GROUP_BY));
    return parsed ?? DEFAULT_H2OGPTE_GROUPBY;
  }, [searchParams]);

  const startTime = useMemo(() => {
    const parsed = parseDateParam(searchParams.get(URL_PARAMS.START_DATE));
    return parsed ?? DEFAULT_START_TIME;
  }, [searchParams]);

  const endTime = useMemo(() => {
    const parsed = parseDateParam(searchParams.get(URL_PARAMS.END_DATE));
    return parsed ?? DEFAULT_END_TIME;
  }, [searchParams]);

  const granularity = useMemo(() => {
    const parsed = parseGranularityParam(searchParams.get(URL_PARAMS.GRANULARITY));
    return parsed ?? DEFAULT_GRANULARITY;
  }, [searchParams]);

  const dateRangeType = useMemo((): DateRangeType => {
    const hasStartDate = searchParams.has(URL_PARAMS.START_DATE);
    const hasEndDate = searchParams.has(URL_PARAMS.END_DATE);
    if (hasStartDate && hasEndDate) {
      return 'custom';
    }
    return DEFAULT_DATE_RANGE_TYPE;
  }, [searchParams]);

  // ============================================
  // Setters that update URL directly
  // ============================================

  const setResourceType = useCallback(
    (resource: ResourceType) => {
      setSearchParams(
        (prev) => {
          const newParams = new URLSearchParams(prev);

          // Resource type - only add if not default
          if (resource !== DEFAULT_RESOURCE) {
            newParams.set(URL_PARAMS.RESOURCE, formatResourceParam(resource));
          } else {
            newParams.delete(URL_PARAMS.RESOURCE);
          }

          // When switching to H2OGPTe with service/tag grouping, reset groupBy
          if (resource === ResourceType.H2OGPTE_TOKENS) {
            const currentGroupBy = parseGroupByParam(prev.get(URL_PARAMS.GROUP_BY));
            if (currentGroupBy === Grouping.SERVICE || currentGroupBy === Grouping.TAG) {
              newParams.delete(URL_PARAMS.GROUP_BY); // Reset to default (workspace)
            }
          }

          return newParams;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const setGroupBy = useCallback(
    (newGroupBy: Grouping) => {
      setSearchParams(
        (prev) => {
          const newParams = new URLSearchParams(prev);

          if (newGroupBy !== DEFAULT_GROUPBY) {
            newParams.set(URL_PARAMS.GROUP_BY, formatGroupByParam(newGroupBy));
          } else {
            newParams.delete(URL_PARAMS.GROUP_BY);
          }

          return newParams;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const setH2OGPTeGroupBy = useCallback(
    (newGroupBy: H2OGPTeGrouping) => {
      setSearchParams(
        (prev) => {
          const newParams = new URLSearchParams(prev);

          if (newGroupBy !== DEFAULT_H2OGPTE_GROUPBY) {
            newParams.set(URL_PARAMS.GROUP_BY, formatH2OGPTeGroupByParam(newGroupBy));
          } else {
            newParams.delete(URL_PARAMS.GROUP_BY);
          }

          return newParams;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const setDateRange = useCallback(
    (newStartTime: Date, newEndTime: Date, newGranularity: Granularity, _newDateRangeType: DateRangeType) => {
      setSearchParams(
        (prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.set(URL_PARAMS.START_DATE, formatDateParam(newStartTime));
          newParams.set(URL_PARAMS.END_DATE, formatDateParam(newEndTime));
          newParams.set(URL_PARAMS.GRANULARITY, formatGranularityParam(newGranularity));
          return newParams;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  return {
    resourceType,
    setResourceType,
    groupBy,
    setGroupBy,
    h2ogpteGroupBy,
    setH2OGPTeGroupBy,
    startTime,
    endTime,
    granularity,
    dateRangeType,
    setDateRange,
  };
}
